import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { isOwnSignIn, kindOfResource, resourceLevel } from "permissions/_views/grant-row/grant-row-model";
import { type RecordKindCount, recordKindCounts } from "permissions/_views/people/person-record-grants-model";
import { kindAllows, kindLabel, recordCount, removedRecordCount } from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";

/**
 * What the person page lists, worked out from the kinds endpoint and the person's standing grants.
 *
 * Plain functions, so the rules — which kinds an area shows, which row a kind draws, what the filters
 * keep, what the summary strip counts — can be read and tested without rendering the page. The design
 * is `docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "The person page".
 */

export type PersonGrantFilter = "all" | "granted" | "not-granted" | "removed";

export const PERSON_GRANT_FILTERS: readonly { value: PersonGrantFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "granted", label: "Granted" },
  { value: "not-granted", label: "Not granted" },
  { value: "removed", label: "Removed" },
];

/** One row of the page: a kind, and the grant it draws, or none for a kind the person does not hold. */
export type PersonKindRow = Readonly<{
  key: string;
  kind: PermissionKind;
  permission?: Permission;
  /** A live grant after the kind's strongest one, drawn with an "Also granted" line. */
  alsoGranted?: boolean;
}>;

export type PersonAreaSection = Readonly<{
  area: string;
  /** Held rows: live grants strongest first, then a removed grant for a kind with no live one. */
  granted: readonly PersonKindRow[];
  /** The kinds the person does not hold and may be granted, folded under "Not granted (n)". */
  notGranted: readonly PersonKindRow[];
  /** One count row per per-record kind the person holds anything of, after the held rows. */
  records: readonly RecordKindCount[];
  /** The same for the per-record kinds with removed grants, which the Removed filter lists. */
  removedRecords: readonly RecordKindCount[];
  grantedCount: number;
  notGrantedCount: number;
  removedCount: number;
}>;

export type PersonGrantsContext = Readonly<{
  /** `PermissionAreas.Names`, in the server's order. */
  areas: readonly string[];
  kinds: readonly PermissionKind[];
  /** The person's standing grants, live and removed. */
  grants: readonly Permission[];
  mayAdministerPlatform: boolean;
  /** The grantee view's count of the person's grants by resource type, for the per-record rows. */
  permissionCounts?: Readonly<Record<string, number>>;
  /** The same count of the person's removed grants. */
  removedPermissionCounts?: Readonly<Record<string, number>>;
}>;

export function standingKinds(kinds: readonly PermissionKind[]): PermissionKind[] {
  return kinds.filter((kind) => !kind.perRecord);
}

/** The resource types the person page reads its standing rows for. */
export function standingResourceTypes(kinds: readonly PermissionKind[]): string[] {
  return [...new Set(standingKinds(kinds).map((kind) => kind.resourceType))];
}

/** Strongest first: the wider scope, then the higher level. */
function strength(permission: Permission): number {
  const { scope, accessLevel } = resourceLevel(permission.resource);
  return (scope ?? Scope["Need-to-Know"]) * 10 + (accessLevel ?? AccessLevel["Read-Only"]);
}

function byStrength(left: Permission, right: Permission): number {
  return strength(right) - strength(left);
}

function byLatestRemoval(left: Permission, right: Permission): number {
  return (right.disabledDate?.valueOf() ?? 0) - (left.disabledDate?.valueOf() ?? 0);
}

function rowKey(kind: PermissionKind, permission?: Permission): string {
  return permission?.id ? permission.id.value : `kind-${kind.slug}`;
}

export function isInherentNotifications(kind: PermissionKind): boolean {
  return kind.resourceType === ResourceType.NotificationsAccess;
}

type KindGrants = Readonly<{ kind: PermissionKind; live: Permission[]; removed: Permission[] }>;

function grantsByKind(context: PersonGrantsContext): KindGrants[] {
  return standingKinds(context.kinds).map((kind) => {
    const own = context.grants.filter((grant) => kindOfResource(context.kinds, grant.resource) === kind);
    return {
      kind,
      live: own.filter((grant) => !grant.isDisabled).sort(byStrength),
      removed: own.filter((grant) => grant.isDisabled).sort(byLatestRemoval),
    };
  });
}

/**
 * Whether a kind belongs on this page at all. A retired kind shows only while it is held, and a
 * platform-wide one only for a platform administrator or while it is held; neither is ever offered.
 */
function isShown(entry: KindGrants, mayAdministerPlatform: boolean): boolean {
  const held = entry.live.length > 0 || entry.removed.length > 0;
  if (entry.kind.retired) return held;
  if (entry.kind.superUserOnly) return held || mayAdministerPlatform;
  return true;
}

/**
 * The page's areas, in the server's order, each with its held rows and its not-granted rows.
 *
 * A standing kind with more than one live grant draws one row per grant, strongest first, and marks
 * the rows after the first as also granted; the server deduplicates only an identical resource. A
 * kind with no live grant draws its latest removed one, so "Grant again" is a switch away; a kind
 * with neither is listed as not granted. Notifications, which everybody holds at need-to-know, count
 * as held even with no row of their own.
 */
export function buildPersonAreas(context: PersonGrantsContext): PersonAreaSection[] {
  const entries = grantsByKind(context).filter((entry) => isShown(entry, context.mayAdministerPlatform));

  return context.areas
    .map((area) => {
      const inArea = entries.filter((entry) => entry.kind.area === area);
      const granted: PersonKindRow[] = [];
      const notGranted: PersonKindRow[] = [];
      let removedCount = 0;

      for (const { kind, live, removed } of inArea) {
        if (live.length > 0) {
          granted.push(
            ...live.map((permission, index) => ({ key: rowKey(kind, permission), kind, permission, alsoGranted: index > 0 })),
          );
        } else if (removed.length > 0) {
          granted.push({ key: rowKey(kind, removed[0]), kind, permission: removed[0] });
          removedCount += 1;
        } else if (isInherentNotifications(kind)) {
          granted.push({ key: rowKey(kind), kind });
        } else if (kind.grantable && !kind.superUserOnly) {
          notGranted.push({ key: rowKey(kind), kind });
        }
      }

      return {
        area,
        granted,
        notGranted,
        records: recordKindCounts(context.kinds, area, context.permissionCounts),
        removedRecords: recordKindCounts(context.kinds, area, context.removedPermissionCounts),
        grantedCount: granted.length - removedCount,
        notGrantedCount: notGranted.length,
        removedCount,
      };
    })
    .filter(hasRows);
}

function hasRows(section: PersonAreaSection): boolean {
  return (
    section.granted.length > 0 ||
    section.notGranted.length > 0 ||
    section.records.length > 0 ||
    section.removedRecords.length > 0
  );
}

/**
 * Every removed grant, latest first, for the Removed filter. A kind re-granted since still has its old
 * row here: that is where a removal stays reachable after it was undone.
 */
function removedRows(context: PersonGrantsContext, area: string): PersonKindRow[] {
  return grantsByKind(context)
    .filter((entry) => entry.kind.area === area && isShown(entry, context.mayAdministerPlatform))
    .flatMap(({ kind, removed }) => removed.map((permission) => ({ key: rowKey(kind, permission), kind, permission })))
    .sort((left, right) => byLatestRemoval(left.permission, right.permission));
}

function matchesText(row: Pick<PersonKindRow, "kind">, area: string, text: string): boolean {
  if (!text) return true;
  const haystack = [kindLabel(row.kind), kindAllows(row.kind), row.kind.targetNoun ?? "", area].join(" ").toLowerCase();
  return text
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

function isLive(row: PersonKindRow): boolean {
  return row.permission ? !row.permission.isDisabled : isInherentNotifications(row.kind);
}

/**
 * The areas the state and text filters leave, with the rows each one keeps. An area with nothing left
 * is dropped, so a filter that matches one kind shows one area.
 *
 * `removedHere` names the rows removed from this page since it opened. They stay where they were under
 * Granted until the reader leaves, so the row they just switched off — and its switch, the way to
 * undo it — does not vanish from under them.
 */
export function filterPersonAreas(
  sections: readonly PersonAreaSection[],
  context: PersonGrantsContext,
  filter: PersonGrantFilter,
  text: string,
  removedHere: ReadonlySet<string> = new Set(),
): PersonAreaSection[] {
  const query = text.trim();
  return sections
    .map((section) => {
      const keep = (row: Pick<PersonKindRow, "kind">) => matchesText(row, section.area, query);
      let granted: PersonKindRow[];
      let notGranted: PersonKindRow[];
      // The per-record counts are of live grants, so they belong under All and Granted. The Removed
      // filter draws the kinds with removed grants in their place, as lists of those alone.
      let records: RecordKindCount[] = [];
      switch (filter) {
        case "granted":
          granted = section.granted.filter((row) => isLive(row) || removedHere.has(row.key));
          notGranted = [];
          records = [...section.records];
          break;
        case "not-granted":
          granted = [];
          notGranted = [...section.notGranted];
          break;
        case "removed":
          granted = removedRows(context, section.area);
          notGranted = [];
          records = [...section.removedRecords];
          break;
        default:
          granted = [...section.granted];
          notGranted = [...section.notGranted];
          records = [...section.records];
      }
      return {
        ...section,
        granted: granted.filter(keep),
        notGranted: notGranted.filter(keep),
        records: records.filter(keep),
        // Drawn under Removed only, as `records`; carried elsewhere it would keep an area with
        // nothing left to draw.
        removedRecords: filter === "removed" ? records.filter(keep) : [],
      };
    })
    .filter(hasRows);
}

/** Signing authority as the server reads it: a live grant of the kind at Read/Write. */
export function holdsSigningAuthority(context: Pick<PersonGrantsContext, "kinds" | "grants">): boolean {
  return liveSigningAuthorityGrants(context).length > 0;
}

export const SIGNING_AUTHORITY_SLUG = "signing-authority";

export function signingAuthorityKind(kinds: readonly PermissionKind[]): PermissionKind | undefined {
  return kinds.find((kind) => kind.slug === SIGNING_AUTHORITY_SLUG);
}

export function liveSigningAuthorityGrants(context: Pick<PersonGrantsContext, "kinds" | "grants">): Permission[] {
  const kind = signingAuthorityKind(context.kinds);
  if (!kind) return [];
  return context.grants.filter(
    (grant) =>
      !grant.isDisabled &&
      kindOfResource(context.kinds, grant.resource) === kind &&
      resourceLevel(grant.resource).accessLevel === AccessLevel["Read/Write"],
  );
}

function plural(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * The summary strip under the person's name: "21 grants in 6 areas · 587 document grants, 12 proposal
 * grants · 2 removed". Built from the enabled standing grants and the per-record counts the grantee view
 * returns; a part with nothing to count is left out rather than printed as zero. The removed count is
 * what the Removed filter reaches: the standing kinds drawn removed, and the removed per-record grants.
 */
export function personSummary(
  sections: readonly PersonAreaSection[],
  kinds: readonly PermissionKind[],
  permissionCounts: Readonly<Record<string, number>> | undefined,
): string[] {
  const grants = sections.reduce((total, section) => total + section.grantedCount, 0);
  const areas = sections.filter((section) => section.grantedCount > 0).length;
  const removed = sections.reduce(
    (total, section) =>
      total + section.removedCount + section.removedRecords.reduce((records, entry) => records + entry.count, 0),
    0,
  );

  const parts = [grants === 0 ? "No standing grants" : `${plural(grants, "grant")} in ${plural(areas, "area")}`];

  const records = recordCounts(kinds, permissionCounts);
  if (records.length > 0) parts.push(records.join(", "));
  if (removed > 0) parts.push(`${removed.toLocaleString()} removed`);
  return parts;
}

/** The per-record grants as counts, "587 document grants", from the grantee view; none for a zero. */
export function recordCounts(
  kinds: readonly PermissionKind[],
  permissionCounts: Readonly<Record<string, number>> | undefined,
): string[] {
  return kinds
    .filter((kind) => kind.perRecord && kind.targetNoun)
    .map((kind) => ({ noun: kind.targetNoun!, count: permissionCounts?.[kind.resourceType] ?? 0 }))
    .filter((entry) => entry.count > 0)
    .map((entry) => recordCount(entry.count, entry.noun));
}

/**
 * What "Remove all access…" lists before it asks for the person's name (#6176): the standing grants
 * by name, the per-record grants as counts, and whether own sign-in is held, which only "Also stop
 * sign-in" removes. Notifications are left out of both lists: the server never removes them, and
 * lowers organization-wide ones to the person's own, which `lowersNotifications` says.
 */
export type RemoveAllAccessReview = Readonly<{
  standing: readonly string[];
  records: readonly string[];
  holdsOwnSignIn: boolean;
  lowersNotifications: boolean;
}>;

export function removeAllAccessReview(
  context: PersonGrantsContext,
  permissionCounts: Readonly<Record<string, number>> | undefined,
): RemoveAllAccessReview {
  const live = grantsByKind(context).filter((entry) => entry.live.length > 0);
  return {
    standing: live
      .filter((entry) => !isInherentNotifications(entry.kind) && !isOwnSignIn(entry.kind))
      .map((entry) => kindLabel(entry.kind)),
    records: recordCounts(context.kinds, permissionCounts),
    holdsOwnSignIn: live.some((entry) => isOwnSignIn(entry.kind)),
    lowersNotifications: live.some(
      (entry) =>
        isInherentNotifications(entry.kind) &&
        entry.live.some((grant) => (resourceLevel(grant.resource).scope ?? Scope["Need-to-Know"]) > Scope["Need-to-Know"]),
    ),
  };
}

/**
 * An area header's counts: "1 granted · 12 proposal grants · 3 not granted". Under Removed the
 * per-record counts are of removed grants, and say so.
 */
export function areaCounts(section: PersonAreaSection, filter: PersonGrantFilter = "all"): string {
  const parts = [`${section.grantedCount} granted`];
  const count = filter === "removed" ? removedRecordCount : recordCount;
  for (const entry of section.records) parts.push(count(entry.count, entry.kind.targetNoun ?? entry.kind.name));
  if (section.notGrantedCount > 0) parts.push(`${section.notGrantedCount} not granted`);
  if (section.removedCount > 0) parts.push(`${section.removedCount} removed`);
  return parts.join(" · ");
}

/**
 * The person's grants as read, with each grant this page granted again that no list holds yet.
 *
 * The page reads live and removed grants in two lists, refetched one at a time after a change. When
 * the removed list comes back first it no longer holds the new grant, and the live list does not hold
 * it yet, so without this the kind fell back to its removed grant for a moment (#6668). A list that
 * holds the grant is newer than this page's copy, so the copy is drawn only while none does.
 */
export function withRegranted(grants: readonly Permission[], regranted: ReadonlyMap<string, Permission>): readonly Permission[] {
  if (regranted.size === 0) return grants;
  const read = new Set(grants.map((grant) => grant.id?.value));
  const missing = [...regranted.values()].filter((grant) => !read.has(grant.id?.value));
  return missing.length === 0 ? grants : [...grants, ...missing];
}
